<template>
    <section class="register__widget-row username">
        <label :class="labelClass">
            {{ $t('registerUsername.username') }}:

            <input id="input-username"
                v-model="username"
                type="text"
                :disabled="isDisabled"
                :placeholder="$t('registerUsername.enterUsername')"
                :class="usernameClass"
            >
            <fieldset v-if="showUsernameSuggestions"
                class="suggestions"
            >
                <span id="suggestions-legend"
                    class="suggestions__legend"
                >
                    {{ $t('registerUsername.suggested') }}
                </span>
                <button v-for="(suggestion, index) in usernameSuggestions"
                    :key="suggestion"
                    aria-describedby="suggestions-legend"
                    type="button"
                    class="suggestions__item"
                    @click="changeUsername(suggestion)"
                >
                    {{ formatSuggestion(suggestion, index) }}
                </button>
            </fieldset>

            <div v-else
                class="username-infos"
            >
                <span :class="characterRangeClass">
                    {{ characterRangeText }}
                </span>
                <span class="character-counter">
                    {{ characterCounterText }}
                </span>
            </div>
            <p class="warnings">
                {{ $t('registerUsername.leaderboardsVisibilityWarning') }}
            </p>
        </label>
    </section>
</template>

<script>
// 3rd party
import axios from 'axios';

// Store
import {
    mapActions,
} from 'vuex';
import localStoreTypes from '@webapp/store/types.js';

// Helpers
import apiUrl from '@webapp/helpers/api-url.js';

const MIN_CHARACTER_COUNT = 3;
const MAX_CHARACTER_COUNT = 15;

let cancel = null,
    timeout = null;

export default {
    name: 'username',
    props: {
        isDisabled: {
            type: Boolean,
            default: false,
        },
        prepopulatedUsername: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            username: '',
            characterRangeClass: 'character-count',
            hasErrors: false,
            isValidating: false,
            usernameSuggestions: [],
        };
    },
    computed: {
        labelClass() {
            return [
                'label', {
                    'label--loading': this.isValidating,
                },
            ];
        },

        usernameClass() {
            return [
                'label__input', {
                    'label__input--active': this.username,
                    'label__input--error': this.usernameSuggestions.length > 0,
                },
            ];
        },

        characterRangeText() {
            return this.$t('registerUsername.characterRangeWarning', {
                min: MIN_CHARACTER_COUNT,
                max: MAX_CHARACTER_COUNT,
            });
        },

        showUsernameSuggestions() {
            return this.usernameSuggestions.length > 0;
        },

        characterCounterText() {
            return `${this.username.length} / ${MAX_CHARACTER_COUNT}`;
        },
    },
    watch: {
        username: 'validateUsername',
    },
    mounted() {
        this.username = this.prepopulatedUsername || '';
    },
    methods: {
        ...mapActions({
            setIsUsernameValid: localStoreTypes.SET_IS_USERNAME_VALID,
            setRegistration: localStoreTypes.SET_REGISTRATION,
        }),

        resetState() {
            this.usernameSuggestions = [];
            this.setIsUsernameValid(false);

        },

        validateUsername(newUsername, oldUsername) {
            if (timeout) {
                clearTimeout(timeout);
            }

            this.resetState();

            if (newUsername.length < MIN_CHARACTER_COUNT && newUsername < oldUsername) {
                this.hasErrors = true;

                this.characterRangeClass = ['character-count', 'character-count--error'];

                return;
            }

            if (newUsername.length > MAX_CHARACTER_COUNT) {
                this.hasErrors = true;
                this.characterRangeClass = ['character-count', 'character-count--error'];
            } else {
                this.characterRangeClass = 'character-count';

                timeout = setTimeout(async() => {
                    if (cancel) {
                        cancel();
                    }

                    this.isValidating = true;

                    try {
                        const { data } = await axios.post(
                            `${apiUrl()}/registration/user/validation/username`,
                            {
                                'username': this.username,
                            },
                            {
                                cancelToken: new axios.CancelToken((c) => {
                                    cancel = c;
                                }),
                            }
                        );

                        this.hasErrors = !data.data.isAvailable || !data.data.isValid;
                        this.usernameSuggestions = data.data.suggestions || [];

                        this.setIsUsernameValid(!this.hasErrors);

                        this.setRegistration([
                            {
                                property: 'username',
                                value: this.username,
                            },
                        ]);
                    } catch (error) {
                        if (axios.isCancel(error)) {
                            console.error('Request canceled by user');

                            return false;
                        }

                        this.setIsUsernameValid(false);

                        const fetchErrors = this.utilGlobalHandleErrorMessages(error);

                        this.$notify({
                            type: 'error',
                            text: fetchErrors.message,
                        });
                    } finally {
                        this.isValidating = false;
                    }
                }, 300);
            }
        },

        formatSuggestion(suggestion, index) {
            if (index === this.usernameSuggestions.length - 1) {
                return suggestion;
            }

            return `${suggestion}, `;
        },

        changeUsername(suggestedUsername) {
            this.username = suggestedUsername;
            this.usernameSuggestions = [];
        },
    },
};
</script>

<style lang="scss" scoped>
.label {
    font-weight: normal;

    &__input {
        margin-bottom: 1.1rem;

        &--error {
            @include input-color($red-orange-color);
        }
    }
}

.suggestions {
    border: none;
    padding: 0;

    &__legend {
        font-size: 1.1rem;
        margin-right: 1ch;
    }

    &__item {
        font-size: 1.1rem;
        margin: 0;
        padding: 0;
        background: none;
        border: none;
        cursor: pointer;
        user-select: none;
        color: $lima-color;

        &:not(:last-of-type) {
            margin-right: 1ch;
        }
    }
}

.username-infos {
    display: flex;
    justify-content: space-between;
}

.character-counter {
    font-family: Nunito;
    font-size: 1.1rem;
    font-weight: bold;
    color: $daintree-color;
}

.character-count {
    font-size: 1.1rem;
    color: $daintree-color;

    &--error {
        color: $red-orange-color;
    }
}

.warnings {
    font-size: 1.1rem;
}
</style>